import { type ClientContext, ContextClient } from '~/services/contextClient';
import AnalyticsClientFactory from './analyticsClient.factory';
import {
    type AnalyticsClientInterface,
    type ContactType,
    type AnalyticsDateRangeParam,
    type UsageType,
    type WorkflowVersionEnteredTotalsParams,
} from './analyticsClient.types';
import type { ExtendedAxiosRequestConfig } from '~/services/BaseClient';

class AnalyticsClient extends ContextClient<AnalyticsClientInterface> {
    constructor() {
        super(AnalyticsClientFactory);
    }

    getMarketingSubscriptionsTotalByStatus(context: ClientContext, type?: ContactType) {
        const client = this.getClient(context);
        return client.getMarketingSubscriptionsTotalByStatus(type);
    }

    getMarketingSubscriptionsTotalOptIns(context: ClientContext, params: AnalyticsDateRangeParam, type?: ContactType) {
        const client = this.getClient(context);
        return client.getMarketingSubscriptionsTotalOptIns(params, type);
    }

    getMarketingSubscriptionsTotalOptOuts(context: ClientContext, params: AnalyticsDateRangeParam, type?: ContactType) {
        const client = this.getClient(context);
        return client.getMarketingSubscriptionsTotalOptOuts(params, type);
    }

    getContactsTotalCreated(context: ClientContext, params: AnalyticsDateRangeParam, type?: ContactType) {
        const client = this.getClient(context);
        return client.getContactsTotalCreated(params, type);
    }

    getMarketingSubscriptionsTotalOptOutsByEntryPoint(context: ClientContext, params: AnalyticsDateRangeParam) {
        const client = this.getClient(context);
        if (client.getMarketingSubscriptionsTotalOptOutsByEntryPoint) {
            return client.getMarketingSubscriptionsTotalOptOutsByEntryPoint(params);
        }
        throw new Error(
            `Marketing subscriptions total opt-outs by entry point is not supported by ${client.constructor.name}`
        );
    }

    getMarketingSubscriptionsTotalOptInsByEntryPoint(context: ClientContext, params: AnalyticsDateRangeParam) {
        const client = this.getClient(context);
        if (client.getMarketingSubscriptionsTotalOptInsByEntryPoint) {
            return client.getMarketingSubscriptionsTotalOptInsByEntryPoint(params);
        }
        throw new Error(
            `Marketing subscriptions total opt-ins by entry point is not supported by ${client.constructor.name}`
        );
    }

    getContactsTotalCreatedByEntryPoint(context: ClientContext, params: AnalyticsDateRangeParam) {
        const client = this.getClient(context);
        if (client.getContactsTotalCreatedByEntryPoint) {
            return client.getContactsTotalCreatedByEntryPoint(params);
        }
        throw new Error(`Contacts total created by entry point is not supported by ${client.constructor.name}`);
    }

    getContactsTotalActiveEntryPoints(context: ClientContext, params?: AnalyticsDateRangeParam) {
        const client = this.getClient(context);
        if (client.getContactsTotalActiveEntryPoints) {
            return client.getContactsTotalActiveEntryPoints(params);
        }
        throw new Error(`Contacts total active entry points is not supported by ${client.constructor.name}`);
    }

    getUsageTotalByType(
        context: ClientContext,
        params: AnalyticsDateRangeParam & { filters: { usage_types: UsageType[] } }
    ) {
        const client = this.getClient(context);
        if (client.getUsageTotalByType) {
            return client.getUsageTotalByType(params);
        }
        throw new Error(`Usage total by type is not supported by ${client.constructor.name}`);
    }

    getWorkflowVersionEnteredTotals(
        context: ClientContext,
        params: WorkflowVersionEnteredTotalsParams,
        config?: ExtendedAxiosRequestConfig
    ) {
        const client = this.getClient(context);
        if (client.getWorkflowVersionEnteredTotals) {
            return client.getWorkflowVersionEnteredTotals(params, config);
        }
        throw new Error(`Workflow version entered totals is not supported by ${client.constructor.name}`);
    }
}

export default new AnalyticsClient();
